import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Badge } from '@/app/components/ui/badge';
import { FieldError } from '@/app/components/ui/field-error';
import {
  useDeleteVendor,
  useUpdateVendor,
  useVendor,
} from '@/hooks/use-procurement';
import { useOrganizationSettings } from '@/hooks/use-settings';
import {
  fieldErrorMessage,
  parseFormErrors,
  summarizeFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import type { UpdateVendorPayload, VendorStatus } from '@/lib/types';
import {
  changedVendorFields,
  VENDOR_FIELD_LABELS,
  vendorErrorToast,
  vendorFormValues,
} from './vendor-form';

export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const vendorId = Number(id);
  const navigate = useNavigate();
  const { data: vendor, isLoading } = useVendor(vendorId);
  const updateVendor = useUpdateVendor();
  const deleteVendor = useDeleteVendor();
  const { data: orgSettings, isLoading: settingsLoading } = useOrganizationSettings();
  const orgCurrency = orgSettings?.currency;

  const savedForm = useMemo(() => (vendor ? vendorFormValues(vendor) : {}), [vendor]);
  // Null until filled from the vendor, and the form isn't rendered before
  // then: Radix Select swaps a value that arrives after it mounts for "".
  const [form, setForm] = useState<UpdateVendorPayload | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const errorFor = (field: string) => fieldErrorMessage(formErrors, field);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (vendor) setForm(savedForm);
  }, [vendor, savedForm]);

  const updateField = <K extends keyof UpdateVendorPayload>(
    field: K,
    value: UpdateVendorPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !vendor) return;
    setFormErrors(null);
    const data = changedVendorFields(form, savedForm);
    // A vendor saved without a currency gets one on save. Unless the user
    // picked one, send blank so the API applies the organisation's current
    // currency (the one shown here may be cached).
    if (!vendor.default_currency.trim() && !('default_currency' in data)) {
      data.default_currency = '';
    }
    try {
      await updateVendor.mutateAsync({ id: vendorId, data });
      toast.success('Vendor saved');
    } catch (error) {
      const errors = parseFormErrors(error, 'The vendor could not be saved. Please try again.');
      setFormErrors(errors);
      toast.error(vendorErrorToast(errors, 'The vendor could not be saved.'));
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this vendor? This cannot be undone.')) return;
    try {
      await deleteVendor.mutateAsync(vendorId);
      navigate('/vendors');
    } catch (error) {
      const errors = parseFormErrors(error, 'The vendor could not be deleted. Please try again.');
      toast.error(summarizeFormErrors(errors, VENDOR_FIELD_LABELS)[0]);
    }
  };

  if (isLoading || settingsLoading || !vendor || !form) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/vendors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {vendor.name}
            {vendor.preferred && (
              <Badge variant="outline" className="text-amber-600 border-amber-600">
                <Star className="h-3 w-3 mr-1 fill-current" />
                Preferred
              </Badge>
            )}
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Vendor Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={form.name ?? ''}
                onChange={(e) => updateField('name', e.target.value)}
                required
                aria-invalid={Boolean(errorFor('name'))}
              />
              <FieldError message={errorFor('name')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_number">Vendor Number</Label>
                <Input
                  id="vendor_number"
                  value={form.vendor_number ?? ''}
                  onChange={(e) => updateField('vendor_number', e.target.value)}
                  aria-invalid={Boolean(errorFor('vendor_number'))}
                />
                <FieldError message={errorFor('vendor_number')} />
              </div>
              <div>
                <Label htmlFor="tax_id">Tax ID</Label>
                <Input
                  id="tax_id"
                  value={form.tax_id ?? ''}
                  onChange={(e) => updateField('tax_id', e.target.value)}
                  aria-invalid={Boolean(errorFor('tax_id'))}
                />
                <FieldError message={errorFor('tax_id')} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={form.status ?? 'active'}
                  onChange={(e) => updateField('status', e.target.value as VendorStatus)}
                  className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                  aria-invalid={Boolean(errorFor('status'))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <FieldError message={errorFor('status')} />
              </div>
              <div>
                <Label htmlFor="default_payment_terms">Payment Terms</Label>
                <Input
                  id="default_payment_terms"
                  value={form.default_payment_terms ?? ''}
                  onChange={(e) => updateField('default_payment_terms', e.target.value)}
                  aria-invalid={Boolean(errorFor('default_payment_terms'))}
                />
                <FieldError message={errorFor('default_payment_terms')} />
              </div>
              <div>
                <Label htmlFor="default_currency">Currency</Label>
                <CurrencySelect
                  id="default_currency"
                  value={
                    form.default_currency ||
                    (vendor.default_currency.trim() ? '' : (orgCurrency ?? ''))
                  }
                  onValueChange={(value) => updateField('default_currency', value)}
                  aria-invalid={Boolean(errorFor('default_currency'))}
                />
                <FieldError message={errorFor('default_currency')} />
                {!vendor.default_currency.trim() ? (
                  <p className="mt-1 text-xs text-amber-700">
                    {`No currency was saved for this vendor. Saving sets it to ${
                      form.default_currency || "your organisation's currency"
                    }.`}
                  </p>
                ) : (
                  !form.default_currency &&
                  !savedForm.default_currency && (
                    <p className="mt-1 text-xs text-amber-700">
                      {`Saved as "${vendor.default_currency}", which is not in the currency list. Choose one to replace it.`}
                    </p>
                  )
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {CURRENCY_NO_CONVERSION_NOTE}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="preferred"
                type="checkbox"
                checked={!!form.preferred}
                onChange={(e) => updateField('preferred', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="preferred" className="cursor-pointer">
                Preferred vendor
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Primary Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={form.contact_name ?? ''}
                  onChange={(e) => updateField('contact_name', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_name'))}
                />
                <FieldError message={errorFor('contact_name')} />
              </div>
              <div>
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  value={form.contact_phone ?? ''}
                  onChange={(e) => updateField('contact_phone', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_phone'))}
                />
                <FieldError message={errorFor('contact_phone')} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={form.contact_email ?? ''}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_email'))}
                />
                <FieldError message={errorFor('contact_email')} />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={form.website ?? ''}
                  onChange={(e) => updateField('website', e.target.value)}
                  aria-invalid={Boolean(errorFor('website'))}
                />
                <FieldError message={errorFor('website')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={form.address ?? ''}
                onChange={(e) => updateField('address', e.target.value)}
                aria-invalid={Boolean(errorFor('address'))}
              />
              <FieldError message={errorFor('address')} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city ?? ''}
                  onChange={(e) => updateField('city', e.target.value)}
                  aria-invalid={Boolean(errorFor('city'))}
                />
                <FieldError message={errorFor('city')} />
              </div>
              <div>
                <Label htmlFor="state_province">State</Label>
                <Input
                  id="state_province"
                  value={form.state_province ?? ''}
                  onChange={(e) => updateField('state_province', e.target.value)}
                  aria-invalid={Boolean(errorFor('state_province'))}
                />
                <FieldError message={errorFor('state_province')} />
              </div>
              <div>
                <Label htmlFor="postal_code">Zip Code</Label>
                <Input
                  id="postal_code"
                  value={form.postal_code ?? ''}
                  onChange={(e) => updateField('postal_code', e.target.value)}
                  aria-invalid={Boolean(errorFor('postal_code'))}
                />
                <FieldError message={errorFor('postal_code')} />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={form.country ?? ''}
                  onChange={(e) => updateField('country', e.target.value)}
                  aria-invalid={Boolean(errorFor('country'))}
                />
                <FieldError message={errorFor('country')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="notes"
              value={form.notes ?? ''}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              aria-invalid={Boolean(errorFor('notes'))}
            />
            <FieldError message={errorFor('notes')} />
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/vendors')}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateVendor.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updateVendor.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>

      {vendor.contacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Contacts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {vendor.contacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between py-2 border-b last:border-b-0"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {contact.name}
                    {contact.is_primary && (
                      <Badge variant="outline" className="ml-2">
                        Primary
                      </Badge>
                    )}
                  </p>
                  {contact.title && (
                    <p className="text-sm text-gray-500">{contact.title}</p>
                  )}
                </div>
                <div className="text-sm text-gray-600 text-right">
                  {contact.email && <p>{contact.email}</p>}
                  {contact.phone && <p>{contact.phone}</p>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {vendor.price_lists.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Price Lists</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {vendor.price_lists.map((pl) => (
              <div
                key={pl.id}
                className="flex items-center justify-between py-2 border-b last:border-b-0"
              >
                <div>
                  <p className="font-medium text-gray-900">{pl.name || `Price list #${pl.id}`}</p>
                  <p className="text-sm text-gray-500">
                    {pl.effective_from ?? '—'} → {pl.effective_to ?? '—'}
                  </p>
                </div>
                <p className="text-sm text-gray-600">{pl.items.length} items</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
